import { useState } from "react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
} from "@mui/material";

import {
  X,
} from "@phosphor-icons/react";

import { QuestionType } from "@/types";
import { isHtmlContentEmpty } from "@/utils/htmlContentStyle";

import { EditingField } from "../../tutor/Tabs/Problems/features";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (
    question: string,
    difficulty: string,
    questionType: QuestionType
  ) => void;
}

interface ValidationErrors {
  question?: string;
}

const CreateProblemModal = ({ isOpen, onClose, onGenerate }: Props) => {
  const [question, setQuestion] = useState("");
  const [errors, setErrors] = useState<{ question?: string }>({});
  const [difficulty, setDifficulty] = useState("easy");
  const [questionType, setQuestionType] = useState(QuestionType.MultipleChoice);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (isHtmlContentEmpty(question)) {
      newErrors.question = "Question is required";
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  const handleGenerate = () => {
    if (validateForm()) {
      onGenerate(question, difficulty, questionType);
      handleClose();
    }
  };

  const resetForm = () => {
    setQuestion("");
    setDifficulty("medium");
    setQuestionType(QuestionType.MultipleChoice);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="text14B">Create Problem</Typography>
        <IconButton
          aria-label="Close create problem dialog"
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: -24,
            right: -24,
            color: "common.white",
          }}
        >
          <X size={18} weight="bold" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <EditingField
          label="Question"
          value={question}
          onChange={(value) => setQuestion(value)}
          type="textarea"
          placeholder="Enter the question"
          isEditing={true}
          initialHeight={200}
          error={!!errors.question}
          labelVariant="text14B"
        />
        <Box sx={{
 mt: 2,
mb: 2 
}}>
          <Typography variant="text14B" sx={{ mb: 1, display: "block" }}>
            Difficulty
          </Typography>
          <ToggleButtonGroup
            value={difficulty}
            exclusive
            onChange={(_event, newValue) => {
              if (newValue !== null) setDifficulty(newValue);
            }}
            size="small"
            fullWidth
            sx={{
              width: "100%",
              gap: 1,
              "& .MuiToggleButtonGroup-grouped": {
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "8px !important",
                textTransform: "none",
                fontWeight: 500,
                color: "text.secondary",
                "&.Mui-selected": {
                  backgroundColor: "rgba(28, 72, 46, 0.08)",
                  borderColor: "secondary.main",
                  color: "secondary.main",
                  "&:hover": {
                    backgroundColor: "rgba(28, 72, 46, 0.12)",
                  },
                  "&.MuiToggleButton-success": {
                    backgroundColor: "rgba(17, 112, 70, 0.08)",
                    borderColor: "additionalGreen.main",
                    color: "additionalGreen.main",
                  },
                  "&.MuiToggleButton-warning": {
                    backgroundColor: "rgba(250, 204, 21, 0.08)",
                    borderColor: "#A16207",
                    color: "#A16207",
                  },
                  "&.MuiToggleButton-error": {
                    backgroundColor: "rgba(196, 18, 48, 0.08)",
                    borderColor: "error.main",
                    color: "error.main",
                  },
                },
              },
            }}
          >
            <ToggleButton value="easy" className="MuiToggleButton-success">
              Easy
            </ToggleButton>
            <ToggleButton value="medium" className="MuiToggleButton-warning">
              Medium
            </ToggleButton>
            <ToggleButton value="hard" className="MuiToggleButton-error">
              Hard
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ mb: 1 }}>
          <Typography variant="text14B" sx={{ mb: 1, display: "block" }}>
            Question Type
          </Typography>
          <ToggleButtonGroup
            value={questionType}
            exclusive
            onChange={(_event, newValue) => {
              if (newValue !== null) setQuestionType(newValue);
            }}
            size="small"
            fullWidth
            sx={{
              width: "100%",
              gap: 1,
              "& .MuiToggleButtonGroup-grouped": {
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "8px !important",
                textTransform: "none",
                fontWeight: 500,
                color: "text.secondary",
                "&.Mui-selected": {
                  backgroundColor: "rgba(28, 72, 46, 0.08)",
                  borderColor: "secondary.main",
                  color: "secondary.main",
                  "&:hover": {
                    backgroundColor: "rgba(28, 72, 46, 0.12)",
                  },
                },
              },
            }}
          >
            <ToggleButton value={QuestionType.MultipleChoice}>
              <img
                src="/images/problems/multiple-choice.svg"
                alt="Multiple Choice"
                className="w-4 h-4 mr-2"
              />
              Multiple Choice
            </ToggleButton>
            <ToggleButton value={QuestionType.TrueFalse}>
              <img
                src="/images/problems/true-false.svg"
                alt="True/False"
                className="w-4 h-4 mr-2"
              />
              True/False
            </ToggleButton>
            <ToggleButton value={QuestionType.ShortAnswer}>
              <img
                src="/images/problems/short-answer.svg"
                alt="Short Answer"
                className="w-4 h-4 mr-2"
              />
              Short Answer
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button
          variant="contained"
          onClick={handleGenerate}
          sx={{
            borderRadius: 1.5,
            px: 3,
            backgroundColor: "secondary.main",
            "&:hover": {
              backgroundColor: "brand.green",
            },
          }}
        >
          Generate
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleClose}
          sx={{
            borderRadius: 1.5,
            px: 3,
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProblemModal;
